/**
 * chat-context — Phase 4 IA-4 page-context auto-injection for Studio chat.
 *
 * `buildPageContext(state)` returns the shape documented in IA §5.5; the
 * chat adapter calls it once per user message and prepends the formatted
 * string to the system prompt sent to the model. The shape is intentionally
 * flat + JSON-serializable so it can also ride on `additionalInstructions`
 * in an assistant-ui ChatModelAdapter path.
 *
 * No React here — this module is consumed from the adapter (which runs
 * inside an async generator) and from tool handlers (which run imperatively).
 */
import type { StudioState } from '@/store/studio-store'
import { getCurrentShell, type StudioShellKind } from '@/store/selectors'

/**
 * Page-context payload injected on every message. Shape matches the
 * IA doc's suggested schema (IA §5.5).
 */
export interface PageContext {
  shell: StudioShellKind
  route: string
  project?: {
    id: string
    name: string
  }
  page?: {
    id: string
    name: string
    surface?: "preview" | "page"
  }
  block?: string
}

/** Build PageContext from a store snapshot. */
export function buildPageContext(state: StudioState): PageContext {
  const shell = getCurrentShell(state)

  // Prefer the live pathname so context reflects what the user is actually
  // looking at, including query params (`?projectId=…`). Fall back to the
  // StudioRoute enum when we're not in a browser (SSR / tests).
  let route: string
  if (typeof window !== 'undefined' && window.location) {
    route = window.location.pathname + window.location.search
  } else {
    route = `/studio/${state.studioRoute === 'home' ? '' : state.studioRoute}`
  }

  const ctx: PageContext = { shell, route }

  if (state.currentProjectId) {
    ctx.project = {
      id: state.currentProjectId,
      name: state.currentProject?.manifest?.name ?? 'Untitled project',
    }
  }

  const selectedPageId = state.chatContext.pageId ?? state.currentPageId ?? undefined
  const selectedPageName = state.chatContext.pageName
  if (selectedPageId && selectedPageName) {
    ctx.page = {
      id: selectedPageId,
      name: selectedPageName,
      ...(state.chatContext.selectionSurface
        ? { surface: state.chatContext.selectionSurface }
        : {}),
    }
  }

  // IDE-only — name of the block currently under the editor cursor.
  const activeBlockId = state.activeBlock?.ref?.blockId
  if (shell === 'ide' && activeBlockId) {
    ctx.block = activeBlockId
  }

  return ctx
}

/**
 * Format PageContext as a plain-text instruction block suitable for
 * prepending to the system prompt. Kept deliberately terse so it doesn't
 * eat the model's context window on long threads.
 */
export function formatContextInstructions(ctx: PageContext): string {
  const lines: string[] = []
  lines.push('[Studio page context]')
  lines.push(`shell: ${ctx.shell}`)
  lines.push(`route: ${ctx.route}`)
  if (ctx.project) {
    lines.push(`project.id: ${ctx.project.id}`)
    lines.push(`project.name: ${ctx.project.name}`)
  }
  if (ctx.page) {
    lines.push(`page.id: ${ctx.page.id}`)
    lines.push(`page.name: ${ctx.page.name}`)
    if (ctx.page.surface) lines.push(`page.surface: ${ctx.page.surface}`)
  }
  if (ctx.block) {
    lines.push(`block: ${ctx.block}`)
  }
  return lines.join('\n')
}
